import {
  AccountCircle as AccountCircleIcon,
  Radio as RadioIcon,
  Event as EventIcon,
  WbSunny as WeatherIcon,
  Article as DeepDiveIcon,
  RssFeed as SubstackIcon,
  Language as NewYorkTimesIcon
} from '@mui/icons-material';
import { Box, Container, Divider, Paper, SelectChangeEvent, Typography, Slider, styled, FormControlLabel, Switch, TextField } from '@mui/material';
import { generateFullProgramPreview } from 'features/websites/api-studio';
import React, { useState, useEffect, useRef } from 'react';
import { prebuiltEndUserProfiles } from '../data/prebuiltProfiles';
import AudioPreview, { AudioPreviewResponse } from '../features/websites/components/common/AudioPreview';
import ListenerProfileSection from '../features/websites/components/studio/sections/ListenerProfileSection';
import { ListenerProfile } from '../types/studio.types';
import { ListenerSettings } from '../features/websites/components/studio/sections/ListenerConfigPanel';
// NEW: imports for website selection UI and API
import { List, ListItem, ListItemButton, Checkbox, ListItemText, CircularProgress, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getWebsites } from 'features/websites/api';
import { Website } from 'types/website.types';

// Local storage keys
const STORAGE_KEYS = {
  PROFILE: 'listener_profile_data',
  SELECTED_PROFILE_ID: 'listener_selected_profile_id',
  ORIGINAL_PROFILE_NAME: 'listener_original_profile_name',  // For tracking the original profile name when customized
  LISTENER_SETTINGS: 'listener_settings_preview-page'  // For storing ListenerConfigPanel settings
};

// Special profile ID for custom profiles
const CUSTOM_PROFILE_ID = 'custom-profile';

// Styled components for enhanced slider appearance
const StyledSlider = styled(Slider)(({ theme }) => ({
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    backgroundColor: theme.palette.primary.main,
    '&:hover, &.Mui-focusVisible': {
      boxShadow: `0 0 0 8px ${theme.palette.primary.main}20`
    }
  },
  '& .MuiSlider-rail': {
    height: 8
  },
  '& .MuiSlider-track': {
    height: 8
  },
  '& .MuiSlider-mark': {
    backgroundColor: theme.palette.primary.main,
    height: 8,
    width: 2,
    marginTop: 0
  }
}));

const ListenerPreviewPage: React.FC = () => {
  // Default values for a new listener profile
  const defaultEndUserProfile: ListenerProfile = {
    name: '',
    interests: [],
    preferredContentTypes: [],
    location: '',
    age: '',
    gender: '',
    preferredPronouns: 'they/them',
    backgroundDescription: '',
    mood: 'Neutral',
    conversationalInformality: 50
  };

  // Default values for listener settings
  const defaultListenerSettings: ListenerSettings = {
    mood: 'Neutral',
    soundscape: 'Silence',
    informality: 50
  };

  // State for the listener profile and selected profile ID
  const [endUserProfile, setEndUserProfile] = useState<ListenerProfile>(defaultEndUserProfile);
  const [selectedEndUserProfileId, setSelectedEndUserProfileId] = useState<string>('');
  const [originalProfileName, setOriginalProfileName] = useState<string>(''); // Track the original profile name
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<string>('');
  
  // News Program Sources
  const [sources, setSources] = useState({
    localNews: true,
    events: false,
    weather: false,
    deepDive: false,
    substack: false,
    newYorkTimes: false
  });
  // REMOVED: numberOfNewsSegments state
  // const [numberOfNewsSegments, setNumberOfNewsSegments] = useState<number>(5);
  const [deepDiveTopic, setDeepDiveTopic] = useState<string>(''); // For Deep Dive option
  // New state: number of articles per segment
  const [numArticles, setNumArticles] = useState<number>(5);

  // NEW: Websites selection state
  const [websites, setWebsites] = useState<Website[]>([]);
  const [websitesLoading, setWebsitesLoading] = useState<boolean>(false);
  const [websitesError, setWebsitesError] = useState<string>('');
  const [selectedWebsiteIds, setSelectedWebsiteIds] = useState<string[]>([]);
  const [websiteSearch, setWebsiteSearch] = useState<string>('');
  
  // Initialize with undefined to prevent overriding localStorage values from ListenerProfileSection
  const [listenerSettings, setListenerSettings] = useState<ListenerSettings | undefined>(undefined);

  // NEW: Load websites for selection
  useEffect(() => {
    const loadWebsites = async () => {
      setWebsitesLoading(true);
      setWebsitesError('');
      try {
        const all = await getWebsites();
        setWebsites(all || []);
      } catch (e) {
        console.error('Failed to load websites:', e);
        setWebsitesError('Failed to load websites');
      } finally {
        setWebsitesLoading(false);
      }
    };
    loadWebsites();
  }, []);

  // NEW: Auto-enable Local News when selecting the first website; auto-disable when none selected
  const prevSelectedCountRef = useRef<number>(0);
  useEffect(() => {
    const prevCount = prevSelectedCountRef.current;
    const newCount = selectedWebsiteIds.length;
    if (prevCount === 0 && newCount > 0) {
      // First selection: turn on Local News automatically
      setSources(prev => (prev.localNews ? prev : { ...prev, localNews: true }));
    } else if (newCount === 0) {
      // No selections: ensure Local News is off
      setSources(prev => (prev.localNews ? { ...prev, localNews: false } : prev));
    }
    prevSelectedCountRef.current = newCount;
  }, [selectedWebsiteIds]);

  // Load profile data from localStorage on component mount
  useEffect(() => {
    try {
      // First, retrieve the saved profile ID
      const savedProfileId = localStorage.getItem(STORAGE_KEYS.SELECTED_PROFILE_ID);
      
      // Load the original profile name if it exists
      const savedOriginalName = localStorage.getItem(STORAGE_KEYS.ORIGINAL_PROFILE_NAME);
      if (savedOriginalName) {
        setOriginalProfileName(savedOriginalName);
      }
      
      // If there's a saved profile ID, set it and check if it corresponds to a pre-built profile
      if (savedProfileId) {
        setSelectedEndUserProfileId(savedProfileId);
        
        // If it's a pre-built profile (not custom), use that profile data
        if (savedProfileId !== CUSTOM_PROFILE_ID) {
          const selectedPrebuiltProfile = prebuiltEndUserProfiles.find(p => p.id === savedProfileId);
          if (selectedPrebuiltProfile && selectedPrebuiltProfile.endUserProfile) {
            setEndUserProfile(selectedPrebuiltProfile.endUserProfile);
            return; // Exit early as we've set the profile from the pre-built profile
          }
        }
      }
      
      // For custom profiles or if no pre-built profile was found:
      // Use the saved custom profile data if available
      const savedProfileJSON = localStorage.getItem(STORAGE_KEYS.PROFILE);
      if (savedProfileJSON) {
        const savedProfile = JSON.parse(savedProfileJSON) as ListenerProfile;
        setEndUserProfile(savedProfile);
      }

      // Load listener settings from localStorage only if they exist
      // This ensures we don't override ListenerProfileSection's values
      const savedListenerSettingsJSON = localStorage.getItem(STORAGE_KEYS.LISTENER_SETTINGS);
      if (savedListenerSettingsJSON) {
        const savedListenerSettings = JSON.parse(savedListenerSettingsJSON) as ListenerSettings;
        setListenerSettings(savedListenerSettings);
      }
    } catch (error) {
      console.error('Error loading profile from localStorage:', error);
      // If there's an error, fall back to default values
    }
  }, []);

  // Save profile data to localStorage whenever it changes
  useEffect(() => {
    try {
      // Only save the profile if it's not empty (at least has some properties set)
      const hasContent = endUserProfile.name || 
                         endUserProfile.location || 
                         (endUserProfile.interests && endUserProfile.interests.length > 0) ||
                         (endUserProfile.preferredContentTypes && endUserProfile.preferredContentTypes.length > 0) ||
                         endUserProfile.backgroundDescription;
      
      if (hasContent) {
        localStorage.setItem(STORAGE_KEYS.PROFILE, JSON.stringify(endUserProfile));
      }
    } catch (error) {
      console.error('Error saving profile to localStorage:', error);
    }
  }, [endUserProfile]);

  // Save selected profile ID to localStorage whenever it changes
  useEffect(() => {
    if (selectedEndUserProfileId) {
      localStorage.setItem(STORAGE_KEYS.SELECTED_PROFILE_ID, selectedEndUserProfileId);
    }
  }, [selectedEndUserProfileId]);

  // Handle profile changes
  const handleProfileChange = (field: keyof ListenerProfile, value: any) => {
    // Update the profile with the new value
    setEndUserProfile({
      ...endUserProfile,
      [field]: value,
    });
    
    // If we're changing a pre-built profile (not already custom), save the original profile name
    if (selectedEndUserProfileId !== CUSTOM_PROFILE_ID && selectedEndUserProfileId !== '') {
      const prebuiltProfile = prebuiltEndUserProfiles.find(p => p.id === selectedEndUserProfileId);
      if (prebuiltProfile) {
        setOriginalProfileName(prebuiltProfile.name);
        localStorage.setItem(STORAGE_KEYS.ORIGINAL_PROFILE_NAME, prebuiltProfile.name);
      }
      
      // Switch to custom profile ID
      setSelectedEndUserProfileId(CUSTOM_PROFILE_ID);
      localStorage.setItem(STORAGE_KEYS.SELECTED_PROFILE_ID, CUSTOM_PROFILE_ID);
    }
    
    // Clear save status when changes are made
    if (saveStatus) setSaveStatus('');
  };

  // Handle listener settings changes
  const handleListenerSettingsChange = (settings: ListenerSettings) => {
    setListenerSettings(settings);
    
    // Also update the corresponding fields in endUserProfile state
    setEndUserProfile(prevProfile => ({
      ...prevProfile,
      mood: settings.mood,
      conversationalInformality: settings.informality
    }));
    
    // We don't need to save to localStorage here as the ListenerProfileSection component handles that
  };

  // Handler for selecting a pre-built profile
  const handleSelectPrebuiltProfile = (event: SelectChangeEvent<string>) => {
    const profileId = event.target.value;
    setSelectedEndUserProfileId(profileId);

    // Clear any original profile tracking when selecting a new profile
    if (profileId !== CUSTOM_PROFILE_ID) {
      setOriginalProfileName('');
      localStorage.removeItem(STORAGE_KEYS.ORIGINAL_PROFILE_NAME);
    }

    if (!profileId) return;

    // If selecting a pre-built profile, load that profile's data
    if (profileId !== CUSTOM_PROFILE_ID) {
      const selectedProfile = prebuiltEndUserProfiles.find(p => p.id === profileId);
      if (!selectedProfile || !selectedProfile.endUserProfile) return;

      setEndUserProfile({
        ...selectedProfile.endUserProfile,
      });
    }

    // Clear save status when a new profile is selected
    if (saveStatus) setSaveStatus('');
  };

  const handleGenerateNewsProgram = async (params: any): Promise<AudioPreviewResponse> => {
    setIsLoading(true);
    try {
      const cleanProfile = {
        name: endUserProfile.name,
        interests: endUserProfile.interests,
        preferredContentTypes: endUserProfile.preferredContentTypes,
        location: endUserProfile.location,
        age: endUserProfile.age,
        gender: endUserProfile.gender,
        preferredPronouns: endUserProfile.preferredPronouns,
        backgroundDescription: endUserProfile.backgroundDescription,
        mood: endUserProfile.mood,
        conversationalInformality: endUserProfile.conversationalInformality,
        // Include all source configurations with their settings
        sources: {
          // CHANGED: replace numberOfSegments with selected website IDs
          localNews: sources.localNews ? { websiteIds: selectedWebsiteIds, numArticles } : null,
          events: sources.events ? {} : null,
          weather: sources.weather ? {} : null,
          deepDive: sources.deepDive ? { topic: deepDiveTopic } : null,
          substack: sources.substack ? {} : null,
          newYorkTimes: sources.newYorkTimes ? {} : null
        }
      };
      
      const response = await generateFullProgramPreview(cleanProfile);
      
      // Format seconds to display as minutes and seconds if greater than 60
      const formatTime = (seconds: number): string => {
        // Round down to ensure no fractions of seconds are displayed
        seconds = Math.floor(seconds);
        
        if (seconds < 60) {
          return `${seconds} seconds`;
        }
        
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes} minute${minutes !== 1 ? 's' : ''} ${remainingSeconds} second${remainingSeconds !== 1 ? 's' : ''}`;
      };
      
      return {
        hlsUrl: response.hlsUrl,
        description: `Scanned through ${response.articleCount} articles from ${response.websiteCount} websites in ${formatTime(response.secondsToGenerate)}.`,
        headers: response.headers
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Function to clear saved profile data
  const clearSavedProfile = () => {
    localStorage.removeItem(STORAGE_KEYS.PROFILE);
    localStorage.removeItem(STORAGE_KEYS.SELECTED_PROFILE_ID);
    localStorage.removeItem(STORAGE_KEYS.ORIGINAL_PROFILE_NAME);
    localStorage.removeItem(STORAGE_KEYS.LISTENER_SETTINGS);
    setEndUserProfile(defaultEndUserProfile);
    setListenerSettings(defaultListenerSettings);
    setSelectedEndUserProfileId('');
    setOriginalProfileName('');
    setSaveStatus('Profile reset to default');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 500 }}>
          Listener Experience Preview
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Hear how listeners in your city will experience Location Station. Use this to refine your website studio configuration.
        </Typography>
      </Box>

      <Paper sx={{ p: 4, mb: 4 }}>
        <ListenerProfileSection
          endUserProfile={endUserProfile}
          selectedEndUserProfileId={selectedEndUserProfileId}
          originalProfileName={originalProfileName}
          customProfileId={CUSTOM_PROFILE_ID}
          isLoading={isLoading}
          onProfileChange={handleProfileChange}
          onSelectPrebuiltProfile={handleSelectPrebuiltProfile}
          listenerSettings={listenerSettings}
          onListenerSettingsChange={handleListenerSettingsChange}
          icon={<AccountCircleIcon sx={{ mr: 1 }} />}
          namespace="preview-page"
        />
        
        {saveStatus && (
          <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
            {saveStatus}
          </Typography>
        )}
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography 
            variant="body2" 
            color="primary" 
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={clearSavedProfile}
          >
            Reset to Default
          </Typography>
        </Box>
      </Paper>

      {/* News Program Generation Section */}
      <Paper sx={{ p: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" component="h2" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <RadioIcon sx={{ mr: 1 }} />
            Local News Program
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate a complete local news program customized for the listener profile and location. 
            This simulates the full end-user experience across multiple news sources for the area.
          </Typography>

          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Select Content Sources:
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
            {/* Local News Source - spans two columns */}
            <Paper sx={{ p: 2, gridColumn: { xs: 'auto', md: '1 / -1' } }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={sources.localNews}
                    onChange={(e) => setSources({ ...sources, localNews: e.target.checked })}
                    disabled={selectedWebsiteIds.length === 0}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RadioIcon sx={{ mr: 1 }} />
                    <Typography>Local News</Typography>
                  </Box>
                }
              />
              {/* When Local News is enabled OR regardless? Show selector always under Local News card */}
              <Box sx={{ pl: 4, mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Select News Sources ({selectedWebsiteIds.length} selected)
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search websites..."
                  value={websiteSearch}
                  onChange={(e) => setWebsiteSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1, maxWidth: 480 }}
                />
                <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto', p: 0 }}>
                  {websitesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" sx={{ ml: 1 }}>Loading websites…</Typography>
                    </Box>
                  ) : websitesError ? (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="error">{websitesError}</Typography>
                    </Box>
                  ) : (
                    <List dense sx={{ py: 0 }}>
                      {websites
                        .filter(w => !websiteSearch || w.name.toLowerCase().includes(websiteSearch.toLowerCase()))
                        .map((w) => {
                          const id = w.id; // use canonical website id
                          const checked = selectedWebsiteIds.includes(id);
                          const toggleSelection = () => {
                            setSelectedWebsiteIds(prev => (
                              checked
                                ? prev.filter(x => x !== id)
                                : Array.from(new Set([...prev, id]))
                            ));
                          };
                          return (
                            <ListItem
                              key={id}
                              secondaryAction={
                                <Checkbox
                                  edge="end"
                                  checked={checked}
                                  onChange={(e) => {
                                    setSelectedWebsiteIds(prev => (
                                      e.target.checked
                                        ? Array.from(new Set([...prev, id]))
                                        : prev.filter(x => x !== id)
                                    ));
                                  }}
                                  inputProps={{ 'aria-label': `select ${w.name}` }}
                                />
                              }
                              sx={{ alignItems: 'flex-start' }}
                              disablePadding
                            >
                              <ListItemButton onClick={toggleSelection} sx={{ alignItems: 'flex-start' }}>
                                {/* Fixed-width logo column to align text consistently */}
                                <Box
                                  sx={{
                                    width: { xs: 120, sm: 160, md: 200 },
                                    minWidth: { xs: 120, sm: 160, md: 200 },
                                    mr: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start'
                                  }}
                               >
                                  {w.logoUrl && (
                                    <Box
                                      component="img"
                                      src={w.logoUrl}
                                      alt={w.name}
                                      sx={{ maxHeight: 28, maxWidth: '100%', objectFit: 'contain' }}
                                    />
                                  )}
                                </Box>

                                {/* Additional fixed spacer before name/URL for consistent left edge */}
                                <Box sx={{ width: 50, minWidth: 50, mr: 1 }} />

                                {/* Name and clickable URL column with consistent left edge */}
                                <ListItemText
                                  primary={
                                    <Box component="span" sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                                      {w.name}
                                    </Box>
                                  }
                                  secondary={w.url ? (
                                    <a
                                      href={w.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {w.url}
                                    </a>
                                  ) : undefined}
                                  secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      {websites && websites.length === 0 && (
                        <Box sx={{ p: 2 }}>
                          <Typography variant="body2" color="text.secondary">No websites available.</Typography>
                        </Box>
                      )}
                    </List>
                  )}
                </Paper>

                {/* Articles per segment slider */}
                <Typography variant="body2" sx={{ mb: 1, mt: 3 }}>
                  Articles per news source: {numArticles}
                </Typography>
                <StyledSlider
                  value={numArticles}
                  onChange={(_, newValue) => setNumArticles(newValue as number)}
                  step={1}
                  marks
                  min={1}
                  max={10}
                  valueLabelDisplay="auto"
                  aria-label="Articles per News Source"
                  size="small"
                  disabled={selectedWebsiteIds.length === 0}
                />

                {/* Fixed-height guidance text area to avoid layout shift */}
                <Box sx={{ mt: 1, minHeight: 20 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', visibility: selectedWebsiteIds.length === 0 ? 'visible' : 'hidden' }}
                  >
                    Select one or more news sources to enable Local News.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Weather Source (left on next row) */}
            <Paper sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={sources.weather}
                    onChange={(e) => setSources({ ...sources, weather: e.target.checked })}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <WeatherIcon sx={{ mr: 1 }} />
                    <Typography>Weather</Typography>
                  </Box>
                }
              />
            </Paper>
            
            {/* Events Source (right on next row) */}
            <Paper sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={sources.events}
                    onChange={(e) => setSources({ ...sources, events: e.target.checked })}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EventIcon sx={{ mr: 1 }} />
                    <Typography>Events</Typography>
                  </Box>
                }
              />
            </Paper>

            {/* Substack (left on third row) */}
            <Paper sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={sources.substack}
                    onChange={(e) => setSources({ ...sources, substack: e.target.checked })}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SubstackIcon sx={{ mr: 1 }} />
                    <Typography>Substack</Typography>
                  </Box>
                }
              />
            </Paper>
 
            {/* New York Times (right on third row) */}
            <Paper sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={sources.newYorkTimes}
                    onChange={(e) => setSources({ ...sources, newYorkTimes: e.target.checked })}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <NewYorkTimesIcon sx={{ mr: 1 }} />
                    <Typography>New York Times</Typography>
                  </Box>
                }
              />
            </Paper>
            
            {/* Deep Dive (left on last row, nothing on right) */}
            <Paper sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={sources.deepDive}
                    onChange={(e) => setSources({ ...sources, deepDive: e.target.checked })}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DeepDiveIcon sx={{ mr: 1 }} />
                    <Typography>Deep Dive</Typography>
                  </Box>
                }
              />
              {sources.deepDive && (
                <Box sx={{ pl: 4, mt: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Deep Dive Topic"
                    variant="outlined"
                    value={deepDiveTopic}
                    onChange={(e) => setDeepDiveTopic(e.target.value)}
                    placeholder="Describe a local issue or topic of interest"
                    multiline
                    rows={3}
                    maxRows={6}
                    sx={{
                      '& .MuiInputBase-root': {
                        alignItems: 'flex-start'
                      },
                      '& .MuiInputBase-input': {
                        resize: 'vertical',
                        minHeight: '100px'
                      }
                    }}
                    helperText="Enter a detailed description of a topic for an in-depth, long-format custom segment. The more specific details you provide, the more tailored the Deep Dive will be."
                  />
                </Box>
              )}
            </Paper>
          </Box>

          <Divider sx={{ my: 3 }} />
          
          <AudioPreview
            buttonTitle="Generate News Program"
            clickedButtonTitle="Generating News Program..."
            handleClickParam={endUserProfile}
            handleClick={handleGenerateNewsProgram}
            heading="Local News Program"
            headingIcon={<RadioIcon />}
            disabled={!endUserProfile.location || !Object.values(sources).some(value => value === true)}
            helperText={!endUserProfile.location 
              ? "Please select a location first to generate a news program" 
              : !Object.values(sources).some(value => value === true)
              ? "Please select at least one content source"
              : "Generate a complete news program based on the listener's profile and location"}
            downloadFilename={endUserProfile.location 
              ? `news-program-${endUserProfile.location.replace(/\s+/g, '-').toLowerCase()}` 
              : 'news-program-default'}
            locationName={endUserProfile.location}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default ListenerPreviewPage;